import { ApolloQueryResult } from "@apollo/client";
import { AsyncSelect } from "@neonumy/ant-design";
import TooltipButton from "views/Documents/components/TooltipButton";
import { MdEdit } from "react-icons/md";
import { clientCentralApiStrawberry, clientNeoFact } from "@neonumy/api-clients";
import { Button, Col, Form, Input, Modal, Row } from "antd";
import { LIST_CLIENT_VAT_TYPES_FOR_ASYNC_SELECT } from "queries/centralApi/clients";
import { LIST_PRODUCTS_TYPES_FOR_ASYNC_SELECT } from "queries/neoFact/catalog";
import { LIST_VATS_FOR_ASYNC_SELECT } from "queries/neoFact/vats";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { FaSave } from "react-icons/fa";
import { useParams } from "react-router-dom";
import { getFormSnakeCaseError } from "utils/getFormError";
import useNotification from "utils/hooks/useNotification";
import useSales from "../../hooks/useSales";
import React from "react";
import { LIST_COUNTERPARTS_QUERY } from "queries/centralApi/counterparts";
import useCoreStore from "views/hooks/useCoreStore";
import { DATE_FORMAT } from "config/settings";
import dayjs from "dayjs";
import useCustomQueryVariables from "../hooks/useCustomQueryVariables";

interface CreateUpdateSaleVatPropsTypes {
  id?: ID;
  data?: GetClientInfoResponseData | undefined;
  refetch: (
    variables?: Partial<ListProductAccountingConfigVars> | undefined,
  ) => Promise<ApolloQueryResult<ListProductAccountingConfigResponse>>;
  onlyCanEditCounterpart?: boolean;
}

export default function CreateUpdateSaleVat(
  props: CreateUpdateSaleVatPropsTypes,
): React.ReactElement {
  const { id, refetch, onlyCanEditCounterpart } = props;

  const urlParams: RouteParams = useParams();
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const companyData = useCoreStore(state => state.companyData);
  const accountingPlanType = companyData?.settings?.accountingPlanType?.value;
  const isSNPlan = accountingPlanType === "SN";
  const hasNotAccountingFirm = companyData?.hasNotAccountingFirm;
  const customQueryVariables = useCustomQueryVariables();

  const [isModalVisible, setIsModalVisible] = useState(false);
  const { createUpdateProductAccounting, getProductAccountingConfig } = useSales(
    id,
    form,
    hasNotAccountingFirm,
  );
  const { notificationSuccessfullySaved } = useNotification();

  useEffect(() => {
    if (id && isModalVisible) {
      getProductAccountingConfig({
        variables: {
          id: id,
        },
      });
    }
  }, [isModalVisible]);

  const onFinish = async (values: Record<string, any>) => {
    const updateId = id ? { id } : {};
    const queryName = id ? "updateProductAccountingConfig" : "createProductAccountingConfig";
    const variables = {
      input: {
        companyId: urlParams.companyId,
        clientVatType: values.clientVatType?.value,
        productType: values.productType?.value,
        vat: values.vat?.value,
        counterpartCode: hasNotAccountingFirm
          ? values.counterpartCode
          : values.counterpartCode?.value,
        ...updateId,
      },
    };

    const response = await createUpdateProductAccounting({ variables });

    const createErrors = response?.data?.createProductAccountingConfig?.errors;
    const updateErrors = response?.data?.updateProductAccountingConfig?.errors;

    if ((response?.data?.[queryName]?.errors?.length ?? 0) > 0) {
      const fieldsData = getFormSnakeCaseError(createErrors || updateErrors, values);
      form.setFields(fieldsData);
    } else {
      refetch();
      notificationSuccessfullySaved(t("Sale vat"));
      setIsModalVisible(false);
      form.resetFields();
    }
  };

  const showModal = () => {
    setIsModalVisible(true);
  };

  const handleOk = () => {
    form.submit();
  };
  const handleCancel = () => {
    setIsModalVisible(false);
  };
  const createOptionCounterPart = (item: Record<string, any>) => {
    const cycleInfo = item?.cycle?.startDate
      ? `${dayjs(item.cycle.startDate).format(DATE_FORMAT)} - ${dayjs(item?.cycle?.endDate || item?.cycle?.limitDate).format(DATE_FORMAT)}`
      : "";

    return {
      label: (
        <div>
          <div>{`${item.code} - ${item.name}`}</div>
          {cycleInfo && <small>{cycleInfo}</small>}
        </div>
      ),
      value: item.code,
    };
  };
  return (
    <>
      {id ? (
        <TooltipButton placement="top" handleOnClick={showModal} icon={<MdEdit />} />
      ) : (
        <Row justify="end" className="margin-top">
          <Button type="primary" onClick={showModal}>
            {t("Add")}
          </Button>
        </Row>
      )}
      <Modal
        title={id ? t("Update sale vat") : t("Add sale vat")}
        open={isModalVisible}
        onOk={handleOk}
        onCancel={handleCancel}
        okText={t("Save")}
        width={768}
        okButtonProps={{
          icon: (
            <span className="anticon">
              <FaSave />
            </span>
          ),
        }}
        className="antd-modal"
      >
        <Form
          name="createUpdateSaleVat"
          layout="vertical"
          form={form}
          onFinish={onFinish}
          autoComplete="off"
        >
          <Row gutter={[16, 16]}>
            {!onlyCanEditCounterpart && (
              <>
                <Col xs={24} sm={12}>
                  <Form.Item
                    name="clientVatType"
                    label={t("Client vat type")}
                    rules={[{ required: true, message: t("This field is required") }]}
                  >
                    <AsyncSelect
                      hasQueryFilter={true}
                      queryOptions={{
                        client: clientNeoFact,
                        dataName: "listClientVatTypes",
                        query: LIST_CLIENT_VAT_TYPES_FOR_ASYNC_SELECT,
                        variables: {
                          filter: {
                            search: "",
                            companyId: urlParams.companyId,
                          },
                          limit: 100,
                        },
                      }}
                      optionsFormat={item => ({
                        label: `${item.code} - ${item.name}`,
                        value: item.id,
                      })}
                      translations={{
                        loadingMessage: t("Loading"),
                        noOptionsMessage: t("No Options"),
                      }}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item
                    name="productType"
                    label={t("Product type")}
                    rules={[{ required: true, message: t("This field is required") }]}
                  >
                    <AsyncSelect
                      queryOptions={{
                        client: clientNeoFact,
                        dataName: "listProductTypes",
                        query: LIST_PRODUCTS_TYPES_FOR_ASYNC_SELECT,
                        variables: {
                          search: "",
                          companyId: urlParams?.companyId,
                        },
                      }}
                      translations={{
                        loadingMessage: t("Loading"),
                        noOptionsMessage: t("No Options"),
                      }}
                      optionsFormat={item => ({
                        label: `${item.code} - ${item.name}`,
                        value: item.id,
                      })}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item
                    name="vat"
                    label={t("VAT rate")}
                    rules={[{ required: true, message: t("This field is required") }]}
                  >
                    <AsyncSelect
                      queryOptions={{
                        client: clientNeoFact,
                        dataName: "listVats",
                        query: LIST_VATS_FOR_ASYNC_SELECT,
                        variables: { search: "", companyId: urlParams?.companyId },
                      }}
                      translations={{
                        loadingMessage: t("Loading"),
                        noOptionsMessage: t("No Options"),
                      }}
                      optionsFormat={item => ({
                        label: `${item.code} - ${item.name} - ${item?.ratePercentage} %`,
                        value: item.id,
                        rate: item?.rate,
                      })}
                    />
                  </Form.Item>
                </Col>
              </>
            )}
            <Col xs={24} sm={12}>
              <Form.Item
                name="counterpartCode"
                label={t("Counterpart VAT")}
                rules={[{ required: true, message: t("This field is required") }]}
              >
                {hasNotAccountingFirm ? (
                  <Input />
                ) : (
                  <AsyncSelect
                    optionsFormat={createOptionCounterPart}
                    hasQueryFilter={true}
                    customQueryVariables={customQueryVariables}
                    queryOptions={{
                      client: clientCentralApiStrawberry,
                      dataName: "listCounterpartsPaginated",
                      query: LIST_COUNTERPARTS_QUERY,
                      variables: {
                        filter: {
                          companyId: urlParams.companyId,
                          isActive: true,
                          search: "",
                          code_Istartswith_In: isSNPlan ? ["443"] : ["445"],
                        },
                        limit: 100,
                      },
                    }}
                    translations={{
                      placeholder: t("Select default counterpart"),
                      loadingMessage: t("Loading..."),
                      noOptionsMessage: t("No Options"),
                    }}
                  />
                )}
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </>
  );
}
